import { useReadyTrigger, useLogger } from '@booksy-com/vue-tools'

import { useCore } from '@/composables/core'

interface DeeplinksServiceInstance {
  handle: (uri: string) => Promise<unknown>
  execute: () => Promise<unknown>
}

interface StoreStateWithApp {
  app: { webView?: string | null }
}

const logger = useLogger('deeplinks')

const noop = (): Promise<unknown> => Promise.resolve()

const fallbackService: DeeplinksServiceInstance = {
  handle: noop,
  execute: noop,
}

let deeplinksService: DeeplinksServiceInstance | null = null
let readyTrigger: ReturnType<typeof useReadyTrigger> | null = null

export const createDeeplinksService = async (): Promise<void> => {
  if (deeplinksService) {
    logger.error('deeplinks service already initialized')
    return
  }

  readyTrigger = useReadyTrigger()
  const { store } = useCore()
  const webView = (store.state as StoreStateWithApp).app.webView

  if (webView) {
    deeplinksService = await import('./service.ts')
      .then(({ deeplinksService: svc }) => svc)
    readyTrigger.resolveTrigger()
    return
  }

  deeplinksService = fallbackService
  readyTrigger.resolveTrigger()
}

const _deeplinksServiceAction = (
  action: keyof DeeplinksServiceInstance
): ((...args: unknown[]) => Promise<unknown>) => {
  const trigger = readyTrigger
  if (!trigger) {
    logger.error('deeplinks service not initialized')
    return noop
  }

  return (...args: unknown[]) =>
    trigger.promise.then(() => {
      const svc = deeplinksService
      if (!svc) {
        logger.error('deeplinks service not initialized')
        return Promise.resolve(undefined)
      }
      if (action === 'handle') return svc.handle(args[0] as string)
      return svc.execute()
    })
}

const handle = (uri: string): Promise<unknown> =>
  _deeplinksServiceAction('handle')(uri)

const execute = (): Promise<unknown> => _deeplinksServiceAction('execute')()

export interface UseDeeplinksServiceResult {
  handle: (uri: string) => Promise<unknown>
  execute: () => Promise<unknown>
}

export const useDeeplinksService = (): UseDeeplinksServiceResult => {
  return {
    handle,
    execute,
  }
}
